// Initial help step component for the onboarding wizard
import { useState, FormEvent, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { SparklesText } from "@/components/ui/sparkles-text"
import { productName } from "@/lib/config"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { supabase } from '../../lib/supabase'
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertCircle, RefreshCcw } from "lucide-react"
import { Tables } from "@/database.types"
import { PulseLoader } from "react-spinners"
import { fetchAnonymousBrandData, getStoredClaimSessionToken } from "@/lib/claimBrand"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useAccountStatus } from '@/hooks/useAccountStatus'
import WebsiteAnalysisModal from '@/components/custom-ui/homepage_loader'

type Brand = Tables<'brands'>

interface HelpWizardStepProps {
  onComplete: () => void
  onBrandUpdate: (data: Partial<Brand>) => void
  skipBilling?: boolean
  existingBrandId?: string | null
}

// Create a wrapper for the onBrandUpdate function that adds a flag to prevent infinite loops
const createSafeBrandUpdater = (onBrandUpdate: (data: Partial<Brand>) => void) => {
  // Track if an update is significant (includes ID) or just a form field edit
  return (data: Partial<Brand>, isSignificantUpdate = false) => {
    // For significant updates (like setting brand ID) or when explicitly marked,
    // pass through the data with ID to ensure proper handling
    if (data.id || isSignificantUpdate) {
      console.log('[DEBUG] Sending significant brand update:', data);
      onBrandUpdate(data);
    } else {
      // For form field edits, mark them specially to prevent unnecessary state resets
      console.log('[DEBUG] Sending non-significant form update:', Object.keys(data));
      
      // Add debugging for form field edits
      console.log('[DEBUG] Form field edit detected - This should NOT cause a state reset');
      
      // WizardLayout will check for this pattern and handle it differently
      onBrandUpdate(data);
    }
  };
};

enum ScrapeStatus {
  NOT_STARTED = "NOT_STARTED",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED"
}

interface WebSiteScrapeResults {
  error?: string;
  errorStack?: string;
  timestamp?: string;
  raw_content?: string;
  extracted_info?: Record<string, any>;
}

export function HelpWizardStep({ onComplete, onBrandUpdate, skipBilling = false, existingBrandId = null }: HelpWizardStepProps) {
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [scrapeStatus, setScrapeStatus] = useState<ScrapeStatus>(ScrapeStatus.NOT_STARTED);
  const [error, setError] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(existingBrandId);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [existingScrapedWebsite, setExistingScrapedWebsite] = useState<string | null>(null);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status, loading: accountLoading } = useAccountStatus();
  
  // Create a safe version of onBrandUpdate that prevents unnecessary rerenders
  const safeBrandUpdate = useRef(createSafeBrandUpdater(onBrandUpdate)).current;
  
  // Check account status and redirect if no active account
  useEffect(() => {
    const checkAccountAndOnboarding = async () => {
      if (!accountLoading && status) {
        // Check if user has no active personal account and is not in onboarding context
        const hasActiveAccount = status.personalAccount?.status === 'active' || 
                                status.personalAccount?.status === 'trialing' ||
                                status.effectiveStatus === 'active';
        
        if (!hasActiveAccount && !skipBilling) {
          // Check if user is in initial onboarding (onboarding_completed = false)
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
              const { data: profile } = await supabase
                .schema('marketing_and_customer_success')
                .from('profiles')
                .select('onboarding_completed')
                .eq('id', session.user.id)
                .single();
              
              // If onboarding is not completed, this is their first time - don't redirect
              if (profile && profile.onboarding_completed === false) {
                console.log('[HelpWizardStep] User in initial onboarding, not redirecting to billing');
                return;
              }
            }
          } catch (error) {
            console.error('[HelpWizardStep] Error checking onboarding status:', error);
          }
          
          console.log('[HelpWizardStep] No active account found, redirecting to billing');
          navigate('/settings/billing');
        }
      }
    };
    
    checkAccountAndOnboarding();
  }, [status, accountLoading, navigate, skipBilling]);
  
  // Log when component receives existingBrandId
  useEffect(() => {
    if (existingBrandId) {
      console.log('[DEBUG] HelpWizardStep received existingBrandId:', existingBrandId);
      setBrandId(existingBrandId);
    }
  }, [existingBrandId]);

  // Check for claimed anonymous brand
  useEffect(() => {
    async function checkClaimedBrand() {
      try {
        // Check if we have a claimed brand from URL params
        const brandIdFromUrl = searchParams.get('brandId');
        if (brandIdFromUrl && brandIdFromUrl !== existingBrandId) {
          console.log('[DEBUG] Found brandId from URL:', brandIdFromUrl);
          setBrandId(brandIdFromUrl);
          safeBrandUpdate({ id: brandIdFromUrl }, true);
          
          // Get the brand details to populate the form
          const { data: brandData, error: brandError } = await supabase
            .from('brands')
            .select('id, url, web_site_scraping_status, status')
            .eq('id', brandIdFromUrl)
            .single();
            
          if (brandError) {
            console.error('[ERROR] Failed to get claimed brand details:', brandError);
          } else if (brandData) {
            console.log('[DEBUG] Found claimed brand details:', brandData);
            if (brandData.url && brandData.web_site_scraping_status === ScrapeStatus.COMPLETED) {
              setExistingScrapedWebsite(brandData.url);
              setWebsiteUrl(brandData.url);
            }
          }
          return;
        }

        // Check for stored session token from brand claiming
        const sessionToken = getStoredClaimSessionToken();
        if (sessionToken && !existingBrandId) {
          console.log('[DEBUG] Found stored claim session token, attempting to fetch anonymous brand data:', sessionToken);
          try {
            const anonymousBrandData = await fetchAnonymousBrandData(sessionToken);
            console.log('[DEBUG] Retrieved anonymous brand data:', anonymousBrandData);
            
            // Pre-populate the website URL if available
            // Note: The URL is stored at the top level, not in brand_data
            if (anonymousBrandData.url) {
              console.log('[DEBUG] Setting website URL from anonymous brand:', anonymousBrandData.url);
              setWebsiteUrl(anonymousBrandData.url);
            }
          } catch (error) {
            console.error('[DEBUG] Failed to fetch anonymous brand data:', error);
            // Clear invalid session token
            localStorage.removeItem('brand_claim_session_token');
          }
        }
      } catch (error) {
        console.error('[ERROR] Exception checking claimed brand:', error);
      }
    }

    checkClaimedBrand();
  }, [existingBrandId, searchParams, safeBrandUpdate]);
  
  // Check if user already has a brand with completed website scraping
  useEffect(() => {
    async function checkExistingBrand() {
      try {
        // If we have an existingBrandId from props, that takes priority
        if (existingBrandId) {
          console.log('[DEBUG] Using existingBrandId from props:', existingBrandId);
          
          // Get the brand details
          const { data: brandData, error: brandError } = await supabase
            .from('brands')
            .select('id, url, web_site_scraping_status, status')
            .eq('id', existingBrandId)
            .single();
            
          if (brandError) {
            console.error('[ERROR] Failed to get brand details:', brandError);
          } else if (brandData) {
            console.log('[DEBUG] Found brand details:', brandData);
            // Only set existing scrape website if status is COMPLETED
            if (brandData.url && brandData.web_site_scraping_status === ScrapeStatus.COMPLETED) {
              setExistingScrapedWebsite(brandData.url);
            }
            setBrandId(brandData.id);
            // Notify parent of the brand ID immediately
            safeBrandUpdate({ id: brandData.id }, true);
          }
          
          setIsLoadingInitialData(false);
          return;
        }

        // Only proceed with finding other brands if no existingBrandId was provided
        const { data: accountData, error: accountError } = await supabase
          .rpc('get_personal_account') as { data: { account_id: string } | null, error: any };

        if (accountError || !accountData?.account_id) {
          console.error('[ERROR] Failed to get personal account:', accountError);
          setIsLoadingInitialData(false);
          return;
        }

        // Check if a brand with this account ID already exists with completed scraping
        const { data: existingBrands, error: searchError } = await supabase
          .from('brands')
          .select('id, url, web_site_scraping_status, status')
          .eq('account_id', accountData.account_id);

        if (searchError) { 
          console.error('[ERROR] Error checking existing brands:', searchError);
        }

        // Log all found brands for debugging
        console.log('[DEBUG] Found brands during initialization:', existingBrands?.map(b => ({
          id: b.id,
          url: b.url,
          scrape_status: b.web_site_scraping_status,
          status: b.status
        })));

        // If any brand exists with completed scraping, use it
        const scrapedBrand = existingBrands?.find(b => 
          b.web_site_scraping_status === ScrapeStatus.COMPLETED);
          
        if (scrapedBrand) {
          console.log('[DEBUG] Found brand with completed scraping:', scrapedBrand.id);
          setExistingScrapedWebsite(scrapedBrand.url);
          setBrandId(scrapedBrand.id);
          
          // Notify parent of the brand ID immediately to prevent orphaned brands
          safeBrandUpdate({ id: scrapedBrand.id }, true);
        }
      } catch (err) {
        console.error('[ERROR] Exception checking existing brand:', err);
      } finally {
        setIsLoadingInitialData(false);
      }
    }

    checkExistingBrand();
  }, [existingBrandId, safeBrandUpdate]);
  
  // Better way to log brandId changes - only logs when it actually changes
  useEffect(() => {
    console.log('brandId', brandId);
  }, [brandId]);
  
  const handleWebScrapingStart = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!websiteUrl) {
      setError("Please enter a valid website URL");
      return;
    }
    
    // Validate URL format
    try {
      // Add protocol if missing
      let normalizedUrl = websiteUrl;
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      new URL(normalizedUrl);
      
      // Open modal and show loading spinner immediately
      setIsModalOpen(true);
      setScrapeStatus(ScrapeStatus.PROCESSING);
      setError(null);

      try {
        // First, check if the URL is reachable
        const session = await supabase.auth.getSession();
        if (!session.data.session) {
          throw new Error('No session found');
        }
        
        const workerUrl = import.meta.env.VITE_WORKERS_URL;
        
        // Check URL reachability before proceeding
        console.log(`[DEBUG] Checking if URL is reachable: ${normalizedUrl}`);
        const reachabilityResponse = await fetch(`${workerUrl}/rainfactory/web-site-scraper/check-url`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ url: normalizedUrl })
        });
        
        if (!reachabilityResponse.ok) {
          throw new Error(`Failed to check URL: ${reachabilityResponse.status}`);
        }
        
        const reachabilityData = await reachabilityResponse.json();
        console.log(`[DEBUG] URL reachability check result:`, reachabilityData);
        
        if (!reachabilityData.success) {
          setScrapeStatus(ScrapeStatus.FAILED);
          setError(`Website is not reachable: ${reachabilityData.message}`);
          return;
        }
        
        // Get account information for brand creation/lookup
        const { data: accountData, error: accountError } = await supabase
          .rpc('get_personal_account') as { data: { account_id: string } | null, error: any };

        if (accountError) {
          throw new Error('Failed to get personal account: ' + accountError.message);
        }

        if (!accountData?.account_id) {
          throw new Error('No personal account found');
        }

        const accountId = accountData.account_id;
        let brandIdToUse: string;

        // Brand ID determination logic - in priority order:
        // 1. Use the brand ID passed from the wizard (highest priority)
        // 2. Use existing brand for this account if found
        // 3. Create a new brand (last resort)
        
        console.log('[DEBUG] Determining which brand to use for scraping');
        console.log('[DEBUG] existingBrandId from props:', existingBrandId);
        console.log('[DEBUG] brandId from state:', brandId);
        
        if (existingBrandId) {
          // TOP PRIORITY: Use the brand ID passed from the wizard
          console.log('[DEBUG] Using brand ID passed from wizard:', existingBrandId);
          brandIdToUse = existingBrandId;
          
          // Update the existing brand with new URL and scraping status
          const { error: updateError } = await supabase
            .from('brands')
            .update({ 
              web_site_scraping_status: ScrapeStatus.NOT_STARTED,
              url: normalizedUrl
            })
            .eq('id', brandIdToUse);
            
          if (updateError) {
            console.error('[ERROR] Error updating existing brand:', updateError);
            throw new Error('Failed to update existing brand: ' + updateError.message);
          }
          
          setScrapeStatus(ScrapeStatus.NOT_STARTED);
          
          // Start scraping process immediately for existing brand
          const response = await fetch(`${workerUrl}/rainfactory/web-site-scraper/scrape`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.data.session.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              brandId: brandIdToUse,
              url: normalizedUrl
            })
          });

          console.log(`[DEBUG] Worker response status for existing brand:`, response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`[ERROR] Worker response error:`, errorText);
            throw new Error(`Failed to scrape website: ${response.status} ${errorText}`);
          }
          
          const responseData = await response.json();
          console.log(`[DEBUG] Worker response data for existing brand:`, responseData);
          
          // If the worker response is successful, ensure we maintain PROCESSING state
          if (responseData.success) {
            console.log('[DEBUG] Setting PROCESSING status after worker call for existing brand');
            setScrapeStatus(ScrapeStatus.PROCESSING);
          } else if (responseData.error) {
            throw new Error(responseData.error);
          }
        } else {
          // If no brand ID was passed, check if a brand exists for this account
          const { data: existingBrand, error: searchError } = await supabase
            .from('brands')
            .select('id, web_site_scraping_status')
            .eq('account_id', accountId)
            .single();

          if (searchError && searchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
            throw new Error(searchError.message);
          }

          if (existingBrand) {
            // SECOND PRIORITY: Use existing brand for this account
            console.log('[DEBUG] Using existing brand from database:', existingBrand.id);
            brandIdToUse = existingBrand.id;

            // Update status to NOT_STARTED
            const { error: updateError } = await supabase
              .from('brands')
              .update({ 
                web_site_scraping_status: ScrapeStatus.NOT_STARTED,
                url: normalizedUrl // Update URL in case it changed
              })
              .eq('id', brandIdToUse);
              
            if (updateError) {
              console.error('[ERROR] Error updating existing brand:', updateError);
              throw new Error('Failed to update existing brand: ' + updateError.message);
            }
              
            setScrapeStatus(ScrapeStatus.NOT_STARTED);
          } else {
            // LAST RESORT: Create a new brand only if no ID was passed and no brand exists
            console.log('[DEBUG] Creating new brand as no existing brand was found');
            const { data: brandData, error: brandError } = await supabase
              .from('brands')
              .insert({
                url: normalizedUrl,
                status: 'ACTIVE',
                web_site_scraping_status: ScrapeStatus.NOT_STARTED,
                brand_name: normalizedUrl.replace(/^https?:\/\//, '').split('/')[0], // Use domain as initial brand name
                account_id: accountId // Add the account_id here
              })
              .select()
              .single();

            if (brandError) {
              throw new Error(brandError.message);
            }

            brandIdToUse = brandData.id;
            // Set the brand ID in state immediately
            setBrandId(brandIdToUse);
            console.log('[DEBUG] Created new brand with ID:', brandIdToUse);
          }
        }

        // Update component state with the selected brand ID
        setBrandId(brandIdToUse);
        
        // Critical: Immediately notify parent of the brand ID to set existingBrandId
        // This ensures BrandWizardStep uses this brand instead of creating a new one
        console.log('[DEBUG] Web scraping using brand with ID:', brandIdToUse);
        safeBrandUpdate({ id: brandIdToUse }, true);

        // Start the scraping process
        // Only start scraping if not already completed
        const { data: brandToScrape } = await supabase
          .from('brands')
          .select('web_site_scraping_status')
          .eq('id', brandIdToUse)
          .single();
          
        if (!brandToScrape?.web_site_scraping_status || 
            brandToScrape.web_site_scraping_status === ScrapeStatus.NOT_STARTED || 
            brandToScrape.web_site_scraping_status === ScrapeStatus.FAILED) {
              
          const response = await fetch(`${workerUrl}/rainfactory/web-site-scraper/scrape`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.data.session.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              brandId: brandIdToUse,
              url: normalizedUrl
            })
          });

          console.log(`[DEBUG] Worker response status:`, response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`[ERROR] Worker response error:`, errorText);
            throw new Error(`Failed to scrape website: ${response.status} ${errorText}`);
          }
          
          const responseData = await response.json();
          console.log(`[DEBUG] Worker response data:`, responseData);
          
          // If the worker response is successful, ensure we maintain PROCESSING state
          if (responseData.success) {
            console.log('[DEBUG] Maintaining PROCESSING status after worker call');
            // We're already in PROCESSING state, so no need to set it again
          } else if (responseData.error) {
            throw new Error(responseData.error);
          }
        } else if (brandToScrape.web_site_scraping_status === ScrapeStatus.COMPLETED) {
          // If scraping was already completed, move to next step
          setTimeout(() => {
            setIsModalOpen(false);
            onComplete();
          }, 1000);
          return;
        }
        
        // Set up polling to check status with improved error handling
        const interval = setInterval(async () => {
          try {
            // Get the brand ID from state, but if not available, use brandIdToUse which we just created
            const currentBrandId = brandId || brandIdToUse;
            
            if (!currentBrandId) {
              console.error('[ERROR] No brand ID available for polling');
              clearInterval(interval);
              setPollingInterval(null);
              return;
            }
            
            // If user initiated cancellation, stop polling
            if (isCanceling) {
              console.log('[DEBUG] Cancellation in progress, stopping polling');
              clearInterval(interval);
              setPollingInterval(null);
              return;
            }
            
            console.log('[DEBUG] Polling for brand status update, brandId:', currentBrandId);
            const { data, error: pollError } = await supabase
              .from('brands')
              .select('web_site_scraping_status, web_site_scraping_results')
              .eq('id', currentBrandId)
              .single();
              
            if (pollError) {
              console.error('[ERROR] Error polling for scraping status:', pollError);
              clearInterval(interval);
              setScrapeStatus(ScrapeStatus.FAILED);
              setError("Error checking scraping status: " + pollError.message);
              return;
            }
            
            if (!data) {
              console.error('[ERROR] No data returned when polling for brand status');
              return;
            }
            
            const status = data.web_site_scraping_status as ScrapeStatus;
            console.log('[DEBUG] Current scraping status:', status);
            
            // Update UI with current status
            setScrapeStatus(status);
            
            // If we have failure results, show the error message
            if (status === ScrapeStatus.FAILED && data.web_site_scraping_results) {
              const results = data.web_site_scraping_results as WebSiteScrapeResults;
              if (results.error) {
                setError(results.error);
              }
            }
            
            if (status === ScrapeStatus.COMPLETED || status === ScrapeStatus.FAILED) {
              console.log('[DEBUG] Scraping process finished with status:', status);
              clearInterval(interval);
              setPollingInterval(null);
              setIsCanceling(false); // Reset cancellation state
              
              if (status === ScrapeStatus.COMPLETED) {
                // Refresh brand details before completing
                const { data: refreshedData, error: refreshError } = await supabase
                  .from('brands')
                  .select('*')
                  .eq('id', currentBrandId)
                  .single();
                  
                if (refreshError) {
                  console.error('[ERROR] Failed to refresh brand data:', refreshError);
                  setError("Failed to load updated brand information");
                  return;
                }

                if (!refreshedData) {
                  console.error('[ERROR] No brand data found after refresh');
                  setError("Failed to load updated brand information");
                  return;
                }

                // Log the refreshed data for debugging
                console.log('[DEBUG] Refreshed brand data:', refreshedData);

                // Store the refreshed data in Supabase cache if needed
                await supabase
                  .from('brands')
                  .upsert(refreshedData);

                // Update parent component with refreshed data and brand ID
                // Ensure we include the ID in this update to maintain consistency
                refreshedData.id = currentBrandId; // Explicitly make sure ID is included
                console.log('Updating brand data after scraping completion:', refreshedData);
                safeBrandUpdate(refreshedData, true);
                
                // Continue with onboarding after a short delay
                setTimeout(() => {
                  setIsModalOpen(false);
                  // Remove redundant brand update - we already sent the full data above
                  // This prevents race conditions or orphaned brands
                  onComplete();
                }, 1500);
              }
            }
          } catch (err) {
            console.error('[ERROR] Exception during status polling:', err);
            clearInterval(interval);
            setScrapeStatus(ScrapeStatus.FAILED);
            setError("Error checking scraping status: " + (err instanceof Error ? err.message : "Unknown error"));
          }
        }, 2000); // Poll more frequently (every 2 seconds instead of 3)
        
        setPollingInterval(interval);
      } catch (err) {
        console.error("Error during web scraping:", err);
        setScrapeStatus(ScrapeStatus.FAILED);
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      }
    } catch (urlError) {
      setError("Please enter a valid website URL");
    }
  };

  // Handle canceling/closing the modal
  const handleCloseModal = async () => {
    // If not in processing state, just close the modal
    if (scrapeStatus !== ScrapeStatus.PROCESSING && scrapeStatus !== ScrapeStatus.NOT_STARTED) {
      setIsModalOpen(false);
      return;
    }
    
    // If in processing state, cancel the scraping process
    const currentBrandId = brandId;
    if (currentBrandId) {
      try {
        console.log('[DEBUG] Canceling scraping process for brandId:', currentBrandId);
        
        // Set canceling state to true - this will stop polling
        setIsCanceling(true);
        
        // Clear polling interval
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        
        // Update the UI to show cancellation in progress
        setScrapeStatus(ScrapeStatus.FAILED);
        setError("Scraping canceled by user");
        
        // Update brand status to FAILED in the database
        const { error } = await supabase
          .from('brands')
          .update({ 
            web_site_scraping_status: ScrapeStatus.FAILED,
            web_site_scraping_results: {
              error: "Scraping canceled by user",
              timestamp: new Date().toISOString()
            }
          })
          .eq('id', currentBrandId);
          
        if (error) {
          console.error('[ERROR] Failed to cancel scraping:', error);
        }
        
        // Try to cancel the worker operation through a dedicated cancellation endpoint
        try {
          const session = await supabase.auth.getSession();
          if (session.data.session) {
            const workerUrl = import.meta.env.VITE_WORKERS_URL;
            
            // Send a cancel request to the worker if the endpoint exists
            // Note: This is a hypothetical endpoint and may need to be implemented on the worker side
            console.log('[DEBUG] Attempting to send cancel signal to worker');
            await fetch(`${workerUrl}/rainfactory/web-site-scraper/cancel`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.data.session.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                brandId: currentBrandId
              })
            }).catch(e => {
              // It's okay if this fails, as the worker might not have a cancel endpoint
              console.log('[DEBUG] Cancel endpoint not implemented or failed:', e);
            });
          }
        } catch (err) {
          console.error('[DEBUG] Error sending cancel signal to worker:', err);
          // Non-critical error, we can continue
        }
      } catch (err) {
        console.error('[ERROR] Exception when canceling scraping:', err);
      }
    } else {
      // Reset state if no brandId
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
    
    // Close the modal
    setIsModalOpen(false);
  };

  // Allow users to try a different website
  const handleReset = () => {
    setExistingScrapedWebsite(null);
    setWebsiteUrl("");
    setError(null);
    setScrapeStatus(ScrapeStatus.NOT_STARTED);
  };

  // Show loading state while checking account status
  if (accountLoading && !skipBilling) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <PulseLoader 
          color="#9E7AFF" 
          size={15}
          margin={4}
          speedMultiplier={0.8}
        />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 relative flex items-start justify-start px-0 py-0">
      {/* Subtle animated background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Soft gradient orbs */}
        <div className="absolute -inset-[10px]">
          <div className="absolute top-0 -left-4 w-48 sm:w-72 h-48 sm:h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
          <div className="absolute top-0 -right-4 w-48 sm:w-72 h-48 sm:h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-48 sm:w-72 h-48 sm:h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />
          <div className="absolute bottom-0 right-20 w-48 sm:w-72 h-48 sm:h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-6000" />
        </div>
      </div>

      {/* Add custom animations to the style tag */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animation-delay-6000 { animation-delay: 6s; }
      `}</style>

      <div className="relative w-full max-w-3xl mx-auto z-10">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl overflow-hidden">

          <div className="relative px-6 py-10 sm:px-10 sm:py-14 md:px-14 md:py-16">
            {/* Icon and Title Section */}
            <div className="text-center mb-8">
              <div className="relative inline-flex items-center justify-center mb-6">
                {/* Soft glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl blur-lg opacity-40 animate-pulse" />
                <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 rounded-2xl shadow-lg transform hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 tracking-tight">
                <span className="block bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {skipBilling
                    ? "Let's Build"
                    : `Welcome to`}
                </span>
                <span className="block bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {skipBilling ? "Something" : ""}
                </span>
                <span className="block mt-2 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
                  {skipBilling ? "Extraordinary" : productName}
                </span>
              </h1>

              {!skipBilling && (
                <div className="mt-6 inline-block">
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300" />
                    <div className="relative px-6 py-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200/50">
                      <p className="text-sm text-purple-700 mb-1 font-medium">Let's start something</p>
                      <SparklesText
                        text="Amazing"
                        className="text-2xl sm:text-3xl font-bold"
                        colors={{ first: "#9333EA", second: "#EC4899" }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Instructions Section */}
            <div className="relative mt-8 mb-8">
              <div className="bg-gradient-to-br from-purple-50/50 to-pink-50/50 rounded-2xl p-6 border border-purple-100/30">
                <p className="text-gray-700 leading-relaxed text-center">
                  <span className="block mb-3 text-xl font-bold text-gray-900">Let's get you started</span>
                  <span className="text-sm sm:text-base">The first step is to provide our system with some important information about your brand so that it can start creating content for you.</span>
                  <span className="block mt-2 text-purple-600 font-medium text-sm">Don't worry, this will be quick and fun!</span>
                </p>

                <div className="mt-6 flex flex-col gap-3">
                  <div className="relative bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-sm sm:text-base">Have a Website?</p>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mt-0.5">We'll analyze it to extract key information automatically</p>
                      </div>
                    </div>
                  </div>

                  <div className="relative bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-sm sm:text-base">No Website?</p>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mt-0.5">You can manually enter the details in the next steps</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
      
      {/* Website URL input form or already analyzed message */}
      {isLoadingInitialData ? (
        <div className="flex justify-center py-6">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-lg opacity-40 animate-pulse" />
            <PulseLoader
              color="#9333EA"
              size={10}
              margin={3}
              speedMultiplier={0.8}
            />
          </div>
        </div>
      ) : existingScrapedWebsite ? (
        <div className="space-y-4 max-w-lg mx-auto">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-green-700 font-semibold text-sm">Website Already Analyzed!</p>
                <p className="text-gray-700 font-medium">{existingScrapedWebsite.replace(/^https?:\/\//, '')}</p>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium text-sm"
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Try a different website
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleWebScrapingStart} className="space-y-4 max-w-lg mx-auto">
          <div className="flex flex-col space-y-3">
            <label htmlFor="website-url" className="text-sm font-semibold text-gray-700 text-center">
              Your Website URL
            </label>
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg blur opacity-0 group-hover:opacity-20 transition duration-300" />
              <Input
                id="website-url"
                type="text"
                placeholder="example.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                className="relative w-full bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 py-3 px-4 text-base rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            <div className="flex justify-center mt-4">
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                disabled={scrapeStatus === ScrapeStatus.PROCESSING}
              >
                {scrapeStatus === ScrapeStatus.PROCESSING ? (
                  <PulseLoader
                    color="white"
                    size={8}
                    margin={3}
                    speedMultiplier={0.8}
                  />
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    Scan my Website
                  </>
                )}
              </Button>
            </div>
            {error && (
              <div className="mt-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm leading-relaxed">
                      {error?.includes("500") ?
                        "We're having trouble accessing your website at the moment." :
                        error?.includes("timeout") ?
                        "Your website is taking longer than expected to respond." :
                        error?.includes("not reachable") || error?.includes("No content returned") ?
                        "We couldn't reach your website or extract content. Please make sure the URL is correct and your website is accessible." :
                        error || "We encountered an unexpected issue while analyzing your website."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
      )}
      
      <div className="text-center mt-6 sm:mt-8">
        {existingScrapedWebsite ? (
          <Button
            onClick={onComplete}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
            size="lg"
          >
            Continue to Next Step
            <svg className="w-4 h-4 ml-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-3 justify-center my-4">
              <div className="h-px bg-gray-300 flex-1 max-w-[60px]" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">or</p>
              <div className="h-px bg-gray-300 flex-1 max-w-[60px]" />
            </div>
            <Button
              onClick={onComplete}
              variant="outline"
              className="border-pink-300 bg-gradient-to-r from-pink-500/10 to-purple-500/10 text-pink-700 hover:from-pink-500/20 hover:to-purple-500/20 hover:border-pink-400 px-6 py-2.5 rounded-lg font-medium transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Continue without website
            </Button>
          </>
        )}
      </div>
      
      {/* Modal for website scraping process */}
      <WebsiteAnalysisModal
        isOpen={isModalOpen && (scrapeStatus === ScrapeStatus.PROCESSING || scrapeStatus === ScrapeStatus.NOT_STARTED) && !isCanceling}
        onClose={handleCloseModal}
        websiteUrl={websiteUrl}
        onComplete={() => {}} // Parent controls the flow
        duration={120000} // 2 minutes for the animation cycle
      />
      
      {/* Error/Success overlay when not showing main animation */}
      {isModalOpen && (
        (scrapeStatus === ScrapeStatus.FAILED || scrapeStatus === ScrapeStatus.COMPLETED || isCanceling) && (
          <Dialog open={true} onOpenChange={setIsModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogTitle>
                {scrapeStatus === ScrapeStatus.COMPLETED ? "Analysis Complete!" : 
                 isCanceling ? "Cancelling..." : "Analysis Issue"}
              </DialogTitle>
              <DialogDescription>
                {scrapeStatus === ScrapeStatus.COMPLETED ? 
                  "We've successfully analyzed your website." :
                  isCanceling ? 
                  "Stopping the analysis process..." :
                  "We encountered an issue during analysis."}
              </DialogDescription>
              
              <div className="py-6">
                {isCanceling && (
                  <div className="flex flex-col items-center space-y-4">
                    <PulseLoader 
                      color="#FF5252" 
                      size={15}
                      margin={4}
                      speedMultiplier={0.8}
                      className="my-4"
                    />
                    <p className="text-center text-sm text-muted-foreground">
                      Cancelling operation...
                    </p>
                  </div>
                )}
                
                {scrapeStatus === ScrapeStatus.COMPLETED && (
                  <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900">
                    <AlertDescription className="text-green-800 dark:text-green-300">
                      Analysis complete! We've extracted key information about your brand. 
                      Continuing to the next step...
                    </AlertDescription>
                  </Alert>
                )}
                
                {scrapeStatus === ScrapeStatus.FAILED && !isCanceling && (
                  <div className="space-y-4">
                    <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <AlertTitle className="text-red-800 dark:text-red-300 font-medium">
                        We ran into a small hiccup
                      </AlertTitle>
                      <AlertDescription className="text-red-700 dark:text-red-200">
                        {error?.includes("500") ? 
                          "We're having trouble accessing your website at the moment. This could be due to high traffic or temporary connectivity issues." :
                          error?.includes("timeout") ?
                          "Your website is taking longer than expected to respond. This might be due to its size or current traffic." :
                          error?.includes("not reachable") || error?.includes("No content returned") ?
                          "We couldn't reach your website or extract content. Please make sure the URL is correct and your website is accessible." :
                          error || "We encountered an unexpected issue while analyzing your website."}
                      </AlertDescription>
                    </Alert>
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <RefreshCcw className="h-4 w-4" />
                        <span>You can try again or continue manually</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4 mt-8">
                  {(scrapeStatus === ScrapeStatus.FAILED || scrapeStatus === ScrapeStatus.COMPLETED) && (
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                      disabled={isCanceling}
                      className="px-6 py-3 bg-white/80 backdrop-blur-sm border-gray-300/50 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      {isCanceling
                        ? "Cancelling..."
                        : "Close"}
                    </Button>
                  )}
                  {scrapeStatus === ScrapeStatus.FAILED && !isCanceling && (
                    <Button
                      onClick={onComplete}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 border-0"
                    >
                      Continue Manually
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )
      )}
          </div>
        </div>
      </div>
    </div>
  )
} 